/**
 * Canvas uPlot explore time series for high-frequency (RAW / 10 Hz) data.
 * Parity with D3 explore: brush zoom (drag), wheel pan when zoomed, dblclick reset, click scrub, upper-left stats.
 */
import { Show, createEffect, createMemo, createSignal, on, onCleanup, onMount } from "solid-js";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import { selectedTime, setSelectedTime, setIsManualTimeChange, requestTimeControl } from "../../store/playbackStore";
import {
  clearActiveSelection,
  clearSelection,
  cutEvents,
  hasSelection,
  isCut,
  selectedRange,
  selectedRanges,
  setHasSelection,
  setSelectedRange,
  isMapBrushProtected,
} from "../../store/selectionStore";
import { getColorByIndex } from "../../utils/colorScale";
import { debug, error as logError } from "../../utils/console";
import {
  findSeriesIndexRangeByTime,
  lineTypeLegendBracket,
  medianSampleIntervalMs,
  plotYFromSeriesPoint,
  sampleRateLegendBracket,
  resolveStickyYRange,
} from "../../utils/timeseriesSeriesTransforms";
import { shouldClearActiveSelectionFirst } from "../../utils/timeSeriesClearAction";
import { LOD_FULL_VIEW_FRACTION } from "../../utils/timeseriesLodController";

const DEFAULT_CHART_HEIGHT_PX = 300;
const MIN_CHART_HEIGHT_PX = 120;
const MAX_CHART_HEIGHT_PX = 1000;
const MARGIN = { top: 25, right: 25, bottom: 25, left: 45 };
/**
 * Vertical chrome each plot adds around its `chartHeight` (the wrap is sized to
 * `chartHeight + top + bottom`). A parent that pins the slot's height MUST add this, or the wrap
 * overflows its container and the bottom time axis is clipped mid-label.
 */
export const EXPLORE_UPLOT_CHART_CHROME_PX = MARGIN.top + MARGIN.bottom;
/** Match PlaybackTimeSeries closest-sample tolerance so union-grid alignment does not null out every y. */
const ALIGN_THRESHOLD_MS = 30_000;

/**
 * Gutter reserved at the right of the plot for `showEdgeValueLabels`, and the gap between the plot edge
 * and the text. Wide enough for a signed two-decimal reading ("-12.95") at the label font, and no wider —
 * the whole point of collapsing the stats table is to give that width back to the trace.
 */
const EDGE_LABEL_GUTTER_PX = 52;
const EDGE_LABEL_GAP_PX = 6;
const EDGE_LABEL_FONT_PX = 11;

function normalizeChartHeight(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {return DEFAULT_CHART_HEIGHT_PX;}
  return Math.min(MAX_CHART_HEIGHT_PX, Math.max(MIN_CHART_HEIGHT_PX, Math.round(parsed)));
}

/** When visible window exceeds this many samples per series, defer legend stats to idle time. */
const HEAVY_LEGEND_WINDOW_POINTS = 50_000;

export type ExploreUPlotBridgeRefs = {
  /** @param syncSelectionStore When false, only reset x scale (store already cleared). */
  resetZoomToFull: (syncSelectionStore?: boolean) => void;
  redraw: () => void;
  /** When `syncSelectionStore` is false (e.g. global time window), only the x scale updates. */
  setZoomDomainFromMs: (startMs: number, endMs: number, syncSelectionStore?: boolean) => void;
  getFullDomainMs: () => [number, number] | null;
  getZoomDomainMs: () => [number, number] | null;
};

/**
 * Structural shapes for the saved chart objects this renderer consumes.
 *
 * Deliberately loose — chart definitions come from the user-object API as free-form JSON, and the
 * renderer only ever reads these fields. Typing them beats the `unknown` casts the upstream copy
 * used, which forced a property access on every line and cost 25 `TS18046`s against a typecheck
 * ratchet that fails on any increase.
 */
type SeriesPointLike = {
  /** Required (not optional) so these points satisfy `closestY`/`sortedData`, which index `x` directly. */
  x: unknown;
  y?: number | null | undefined;
  displayY?: number | null | undefined;
  [key: string]: unknown;
};

type SeriesLike = {
  label?: string;
  yaxis?: { name?: string };
  color?: unknown;
  data?: SeriesPointLike[];
  lineType?: unknown;
  dataResample?: unknown;
  chartHeight?: unknown;
  [key: string]: unknown;
};

type ChartLike = {
  series?: SeriesLike[];
  chartHeight?: unknown;
  [key: string]: unknown;
};

function seriesDisplayName(series: { label?: string; yaxis?: { name?: string } }): string {
  return series?.label ?? series?.yaxis?.name ?? "Series";
}

/**
 * Channel out of a fleet series name. Fleet labels are `"<channel> - <BOAT>"` — boat identity rides on the
 * name and is recovered by splitting on `" - "` (same rule as `fleetTeamNameFromSeries` in TimeSeries and
 * `channelFromSeriesName` in FleetTimeseries).
 */
function channelNameFromLabel(label: string): string {
  const idx = label.indexOf(" - ");
  return idx >= 0 ? label.slice(0, idx).trim() : label.trim();
}

/** Channel caption geometry, in CSS px (scaled by the plot's pxRatio at draw time). */
const CHANNEL_LABEL_FONT_PX = 13;
const CHANNEL_LABEL_LEFT_PX = 12;
const CHANNEL_LABEL_TOP_PX = 8;
const CHANNEL_LABEL_LINE_PX = 15;

type ChartSlotPlaceholder = {
  kind: "placeholder";
  index: number;
  chartHeight: number;
  labels: string[];
};

type ChartSlotPlot = {
  kind: "plot";
  index: number;
  built: BuiltChart;
};

type ChartSlot = ChartSlotPlaceholder | ChartSlotPlot;

function buildChartSlots(charts: ChartLike[]): ChartSlot[] {
  return (charts ?? []).map((ch, index) => {
    const b = buildChartAligned(ch);
    if (b) {
      return { kind: "plot" as const, index, built: b };
    }
    const labels = (ch.series ?? []).map((s: { label?: string; yaxis?: { name?: string } }) =>
      seriesDisplayName(s),
    );
    return {
      kind: "placeholder" as const,
      index,
      chartHeight: normalizeChartHeight(ch.chartHeight),
      labels,
    };
  });
}

function mountPlaceholderSlot(wrap: HTMLDivElement, slot: ChartSlotPlaceholder): void {
  wrap.className = "explore-uplot-chart-wrap explore-uplot-chart-placeholder";
  wrap.dataset.chartIndex = String(slot.index);
  wrap.replaceChildren();

  const chartSlotHeight = slot.chartHeight + MARGIN.top + MARGIN.bottom;
  wrap.style.height = `${chartSlotHeight}px`;
  wrap.style.minHeight = `${chartSlotHeight}px`;
  wrap.style.flexShrink = "0";

  const frame = document.createElement("div");
  frame.className = "explore-uplot-placeholder-frame";

  if (slot.labels.length > 0) {
    const channels = document.createElement("div");
    channels.className = "explore-uplot-placeholder-channels";
    channels.textContent = slot.labels.join(" · ");
    frame.appendChild(channels);
  }

  const loading = document.createElement("div");
  loading.className = "timeseries-chart-loading-row explore-uplot-placeholder-loading";
  loading.setAttribute("role", "status");
  loading.setAttribute("aria-live", "polite");

  const spinner = document.createElement("span");
  spinner.className = "timeseries-chart-loading-spinner";
  spinner.setAttribute("aria-hidden", "true");

  const label = document.createElement("span");
  label.className = "timeseries-chart-loading-label";
  label.textContent = "Loading…";

  loading.appendChild(spinner);
  loading.appendChild(label);
  frame.appendChild(loading);
  wrap.appendChild(frame);
}

function pointMs(x: unknown): number {
  if (x instanceof Date) {return x.getTime();}
  if (typeof x === "number" && Number.isFinite(x)) {
    const ax = Math.abs(x);
    // Heuristic: unix seconds ~1e9–1e10; epoch ms ~1e12–1e13 (prevents wrong 1970-era dates).
    if (ax > 0 && ax < 1e11) {return x * 1000;}
    return x;
  }
  if (typeof x === "string") {
    const t = x.trim();
    if (/^-?\d+(\.\d+)?$/.test(t)) {
      const n = Number(t);
      if (Number.isFinite(n)) {
        const ax = Math.abs(n);
        if (ax > 0 && ax < 1e11) {return n * 1000;}
        return n;
      }
    }
  }
  return new Date(x as string).getTime();
}

function formatStat(val: number | undefined | null): string {
  if (val === undefined || val === null || Number.isNaN(val) || !Number.isFinite(val)) {return "N/A";}
  return String(Number(Math.round(Number(val) * 100) / 100));
}

function indexAtOrBefore(data: Array<{ x: unknown }>, tMs: number): number {
  let lo = 0;
  let hi = data.length - 1;
  if (hi < 0 || pointMs(data[0].x) > tMs) {return -1;}
  if (pointMs(data[hi].x) <= tMs) {return hi;}
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (pointMs(data[mid].x) <= tMs) {lo = mid;}
    else {hi = mid;}
  }
  return lo;
}

function closestY(
  sorted: Array<{ x: unknown; y?: number | null | undefined; displayY?: number | null | undefined }>,
  tMs: number,
  thresholdMs: number,
  lineType?: unknown
): number | null {
  const pointValue = (p: { y?: number | null | undefined; displayY?: number | null | undefined }): number | null =>
    plotYFromSeriesPoint(p, lineType);
  const i = indexAtOrBefore(sorted, tMs);
  if (i < 0) {
    if (sorted.length === 0) {return null;}
    const d = pointMs(sorted[0].x);
    if (Math.abs(d - tMs) > thresholdMs) {return null;}
    return pointValue(sorted[0]);
  }
  const a = sorted[i];
  const aMs = pointMs(a.x);
  const ya = pointValue(a);
  if (i === sorted.length - 1) {
    return Math.abs(aMs - tMs) <= thresholdMs ? ya : null;
  }
  const b = sorted[i + 1];
  const bMs = pointMs(b.x);
  const yb = pointValue(b);
  const da = Math.abs(aMs - tMs);
  const db = Math.abs(bMs - tMs);
  const pickA = da <= db;
  const dist = pickA ? da : db;
  if (dist > thresholdMs) {return null;}
  return pickA ? ya : yb;
}

type ExploreInteractionState = {
  hoverLeft: number | null;
  brushLeft: number | null;
  brushWidth: number | null;
  /** True while pointer is down and user may be drag-brushing (skips heavy playhead draw). */
  brushDragging: boolean;
};

type BuiltChart = {
  data: uPlot.AlignedData;
  seriesMeta: {
    label: string;
    color: string;
    lineType?: unknown;
    rawSeries: unknown;
    sortedData: Array<{ x: unknown; y?: number | null | undefined; displayY?: number | null | undefined }>;
  }[];
  fullMinSec: number;
  fullMaxSec: number;
  chartHeight: number;
};

function seriesDataChronological(data: unknown[]): boolean {
  if (data.length < 2) {return true;}
  let prev = pointMs((data[0] as { x?: unknown }).x);
  const step = Math.max(1, Math.floor(data.length / 64));
  for (let i = step; i < data.length; i += step) {
    const t = pointMs((data[i] as { x?: unknown }).x);
    if (Number.isFinite(prev) && Number.isFinite(t) && t < prev) {return false;}
    prev = t;
  }
  const last = pointMs((data[data.length - 1] as { x?: unknown }).x);
  return !(Number.isFinite(prev) && Number.isFinite(last) && last < prev);
}

/**
 * Seconds from the first to the last timestamp where some series has a value. The timeline also carries
 * rows where every plotted series is null (a boat whose day starts earlier, channels missing on that
 * source); counting them stretched the axis over empty hours and squeezed the data into the right edge.
 * Falls back to the whole timeline when no series has a value.
 */
function occupiedXRangeSec(xSec: number[], yCols: (number | null)[][]): [number, number] {
  const has = (i: number) => yCols.some((ys) => ys[i] != null);
  let lo = 0;
  while (lo < xSec.length && !has(lo)) {lo += 1;}
  if (lo === xSec.length) {return [xSec[0]!, xSec[xSec.length - 1]!];}
  let hi = xSec.length - 1;
  while (hi > lo && !has(hi)) {hi -= 1;}
  return [xSec[lo]!, xSec[hi]!];
}

/** Merged explore fetch: all series share one timeline — skip Set union + closestY scan. */
function buildChartAlignedSharedTimeline(seriesList: SeriesLike[]): BuiltChart | null {
  if (seriesList.length === 0) {return null;}
  const firstData = seriesList[0]?.data ?? [];
  if (!Array.isArray(firstData) || firstData.length === 0) {return null;}

  const n = firstData.length;
  for (let si = 1; si < seriesList.length; si++) {
    const d = seriesList[si]?.data;
    if (!Array.isArray(d) || d.length !== n) {return null;}
    if (!seriesDataChronological(d)) {return null;}
  }
  if (!seriesDataChronological(firstData)) {return null;}

  const xSec: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const t = pointMs(firstData[i]?.x);
    if (!Number.isFinite(t)) {return null;}
    xSec[i] = t / 1000;
  }

  const seriesMeta: BuiltChart["seriesMeta"] = [];
  const yCols: (number | null)[][] = [];

  seriesList.forEach((s: SeriesLike, si: number) => {
    const label = seriesDisplayName(s);
    const color = typeof s.color === "string" && s.color ? s.color : getColorByIndex(si);
    const data = s.data ?? [];
    seriesMeta.push({
      label,
      color,
      lineType: s.lineType,
      rawSeries: s,
      sortedData: data,
    });
    const ys: (number | null)[] = new Array(n);
    for (let i = 0; i < n; i++) {
      ys[i] = plotYFromSeriesPoint(data[i], s.lineType);
    }
    yCols.push(ys);
  });

  const [fullMinSec, fullMaxSec] = occupiedXRangeSec(xSec, yCols);
  return {
    data: [xSec, ...yCols] as uPlot.AlignedData,
    seriesMeta,
    fullMinSec,
    fullMaxSec,
    chartHeight: normalizeChartHeight(seriesList[0]?.chartHeight),
  };
}

function buildChartAligned(chart: ChartLike): BuiltChart | null {
  const seriesList = chart.series ?? [];
  if (seriesList.length === 0) {return null;}

  const shared = buildChartAlignedSharedTimeline(seriesList);
  if (shared) {
    shared.chartHeight = normalizeChartHeight(chart.chartHeight);
    return shared;
  }

  const timeSet = new Set<number>();
  for (const s of seriesList) {
    for (const p of s.data ?? []) {
      const t = pointMs(p.x);
      if (Number.isFinite(t)) {timeSet.add(t);}
    }
  }
  const timesMs = [...timeSet].sort((a, b) => a - b);
  if (timesMs.length === 0) {return null;}

  const xSec = timesMs.map((t) => t / 1000);

  const seriesMeta: BuiltChart["seriesMeta"] = [];
  const yCols: (number | null)[][] = [];

  seriesList.forEach((s: SeriesLike, si: number) => {
    const label = seriesDisplayName(s);
    const color = typeof s.color === "string" && s.color ? s.color : getColorByIndex(si);
    const sorted = [...(s.data ?? [])].sort((a: SeriesPointLike, b: SeriesPointLike) => pointMs(a.x) - pointMs(b.x));
    seriesMeta.push({ label, color, lineType: s.lineType, rawSeries: s, sortedData: sorted });
    const ys = timesMs.map((t) => closestY(sorted, t, ALIGN_THRESHOLD_MS, s.lineType));
    yCols.push(ys);
  });

  const [fullMinSec, fullMaxSec] = occupiedXRangeSec(xSec, yCols);
  return {
    data: [xSec, ...yCols] as uPlot.AlignedData,
    seriesMeta,
    fullMinSec,
    fullMaxSec,
    chartHeight: normalizeChartHeight(chart.chartHeight),
  };
}

/**
 * Each sample gets a small dot once zoomed in far enough to tell samples apart, by the series' own grid
 * in view: 100 Hz under 1 s, 10 Hz (and 1 Hz) under 10 s. The grid is read off the visible samples, since
 * level of detail mixes a 1 Hz base with finer windows. uPlot re-evaluates `points.show` on every redraw,
 * so this follows zoom without rebuilding the plot. x is seconds.
 */
const SAMPLE_DOTS_MAX_SPAN_S_RAW = 1;
const SAMPLE_DOTS_MAX_SPAN_S = 10;
const SAMPLE_DOT_SIZE_PX = 5;

/**
 * Longest a brush zoom waits for finer data before zooming on what is loaded (which then swaps to the finer
 * data when it lands — visible as a flash). Generous: the first brush of a session hits a cold file server.
 */
const BRUSH_DETAIL_WAIT_MS = 6000;
/** After the parent has handed the data down, longest to wait for it to reach the plot. */
const BRUSH_DATA_SYNC_WAIT_MS = 2000;

/**
 * Wait cursor over the whole page while a brush zoom loads and redraws. A class on <html> rather than a
 * per-plot style: the hover handler rewrites the plot's own cursor on every move, and every chart in the
 * stack updates. Counted, so overlapping brushes / several charts never clear it early.
 */
const BRUSH_BUSY_CLASS = "explore-uplot-busy";
let brushBusyCount = 0;
function acquireBrushBusyCursor(): () => void {
  brushBusyCount += 1;
  document.documentElement.classList.add(BRUSH_BUSY_CLASS);
  let released = false;
  return () => {
    if (released) {return;}
    released = true;
    brushBusyCount = Math.max(0, brushBusyCount - 1);
    if (brushBusyCount === 0) {document.documentElement.classList.remove(BRUSH_BUSY_CLASS);}
  };
}

/** Median spacing (s) of up to ~100 consecutive non-null samples of `seriesIdx` around the middle of [i0, i1]. */
function visibleSeriesSpacingS(u: uPlot, seriesIdx: number, i0: number, i1: number): number | null {
  const xs = u.data[0] as ArrayLike<number>;
  const ys = u.data[seriesIdx] as ArrayLike<number | null | undefined> | undefined;
  if (!xs || !ys || !(i1 > i0)) {return null;}
  const gaps: number[] = [];
  let prevX: number | null = null;
  for (let i = Math.max(i0, ((i0 + i1) >> 1) - 200); i <= i1 && gaps.length < 100; i++) {
    if (ys[i] == null) {continue;}
    const x = xs[i];
    if (prevX != null && x > prevX) {gaps.push(x - prevX);}
    prevX = x;
  }
  if (gaps.length === 0) {return null;}
  gaps.sort((a, b) => a - b);
  return gaps[gaps.length >> 1];
}

function showSampleDots(u: uPlot, seriesIdx: number, i0: number, i1: number): boolean {
  const mn = u.scales.x.min;
  const mx = u.scales.x.max;
  if (mn == null || mx == null) {return false;}
  const span = mx - mn;
  if (span >= SAMPLE_DOTS_MAX_SPAN_S) {return false;}
  if (span < SAMPLE_DOTS_MAX_SPAN_S_RAW) {return true;}
  // Between 1 s and 10 s: only a series sampled at 10 Hz or slower (spacing ≥ ~50 ms).
  const dt = visibleSeriesSpacingS(u, seriesIdx, i0, i1);
  return dt != null && dt >= 0.05;
}

/**
 * Wall-clock tick label. `fracDigits` > 0 appends the sub-second part (".1", ".25", ".125") — used when the
 * tick step is under a second, i.e. zoomed into a 10 Hz / 100 Hz window.
 */
function formatAxisTime(sec: number, tz: string | null, fracDigits = 0): string {
  if (fracDigits > 0) {
    const totalMs = Math.round(sec * 1000);
    const whole = formatAxisTime(Math.floor(totalMs / 1000), tz);
    const ms = String(((totalMs % 1000) + 1000) % 1000).padStart(3, "0");
    return `${whole}.${ms.slice(0, fracDigits)}`;
  }
  const d = new Date(sec * 1000);
  if (tz) {
    try {
      return d.toLocaleTimeString(undefined, {
        timeZone: tz,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      /* fall through */
    }
  }
  return d.toISOString().slice(11, 19);
}

/** Day label ("Oct 14") for day-step ticks. */
function formatAxisDate(sec: number, tz: string | null): string {
  const d = new Date(sec * 1000);
  try {
    return d.toLocaleDateString(undefined, { timeZone: tz ?? "UTC", month: "short", day: "numeric" });
  } catch {
    return d.toISOString().slice(5, 10);
  }
}

/** uPlot instances expose `pxRatio` at runtime; typings may omit it. */
/** Pointer proximity (CSS px) within which a pointerdown grabs the red selected-time line for scrubbing. */
const PLAYHEAD_GRAB_PX = 8;

function plotPxRatio(u: uPlot): number {
  const r = (u as unknown as { pxRatio?: number }).pxRatio;
  return typeof r === "number" && Number.isFinite(r) && r > 0 ? r : window.devicePixelRatio || 1;
}

/** Absolute canvas X for time `sec` (use `valToPos(..., true)`; do not add `u.bbox.left`). */
function plotXFromTimeSec(u: uPlot, sec: number): number {
  return u.valToPos(sec, "x", true);
}

/**
 * Plot-relative **canvas** x in [0, u.bbox.width] from a pointer on `.u-over`.
 * Use with `u.posToVal(u.bbox.left + x, "x", true)` (see uPlot `posToVal` / `valToPos` third arg: canvas pixels).
 *
 * 1. Visual → layout: `(clientX - rect.left) * (offsetWidth / rect.width)` (ancestor CSS scale).
 * 2. Layout → canvas: `× u.pxRatio` (matches uPlot bbox and draw hooks).
 */
function correctedPointerPlotX(target: HTMLElement, clientX: number, plotWidth: number, u: uPlot): number {
  const rect = target.getBoundingClientRect();
  if (!(rect.width > 0)) {
    return 0;
  }
  const pxRatio = plotPxRatio(u);
  const scaleX = target.offsetWidth > 0 ? target.offsetWidth / rect.width : 1;
  const layoutX = (clientX - rect.left) * scaleX;
  const canvasX = layoutX * pxRatio;
  return Math.max(0, Math.min(plotWidth, canvasX));
}

function seriesPointValue(
  p: {
    y?: number | null | undefined;
    displayY?: number | null | undefined;
  },
  lineType?: unknown
): number | null {
  return plotYFromSeriesPoint(p, lineType);
}

function statsForSeries(
  sortedData: Array<{
    x: unknown;
    y?: number | null | undefined;
    displayY?: number | null | undefined;
  }>,
  visLoMs: number,
  visHiMs: number,
  selMs: number | null,
  lineType?: unknown
): { min?: number; max?: number; avg?: number; std?: number; sel?: number } {
  if (!sortedData?.length) {return {};}
  const { start, end } = findSeriesIndexRangeByTime(sortedData, visLoMs, visHiMs);
  if (start > end) {return {};}

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let i = start; i <= end; i++) {
    const v = seriesPointValue(sortedData[i], lineType);
    if (v == null) {continue;}
    if (v < min) {min = v;}
    if (v > max) {max = v;}
    sum += v;
    sumSq += v * v;
    count++;
  }
  if (count === 0) {return {};}

  const avg = sum / count;
  const variance = Math.max(0, sumSq / count - avg * avg);
  const std = Math.sqrt(variance);

  let sel: number | undefined;
  if (selMs != null && Number.isFinite(selMs)) {
    const cy = closestY(sortedData, selMs, Number.POSITIVE_INFINITY, lineType);
    if (cy != null) {sel = cy;}
  }

  return { min, max, avg, std, sel };
}

export interface ExploreTimeSeriesUPlotProps {
  charts: ChartLike[];
  timezone: string | null;
  /** Dataset explore stacks charts in one root; fleet mounts one uPlot per `.time-series-single-chart`. */
  layout?: "stack" | "fleet";
  /** Bumped by parent when chart count / layout / timezone changes — full slot rebuild. */
  visualEpoch: number;
  /** Bumped by parent on progressive data updates — incremental setData / slot promotion. */
  dataRevision?: number;
  getHostElement: () => HTMLElement | null;
  onBridgeReady: (refs: ExploreUPlotBridgeRefs | null) => void;
  onZoomedChange: (zoomed: boolean) => void;
  /**
   * Visible x window in epoch ms. 'live' fires on every x scale change — mid-drag, mid-wheel and zooms
   * driven from the selection store — so a parent can start loading finer data while the user is still
   * zooming; 'settled' fires once a user gesture ends.
   */
  onVisibleRangeChange?: (minMs: number, maxMs: number, phase: "live" | "settled", fullView: boolean) => void;
  /**
   * Called on brush release, before the zoom is applied. A parent that lazy-loads finer data for the
   * window returns a promise: true once that data has been handed down (wait for it to reach the plot),
   * false when nothing changed. The zoom then lands straight on the finer data instead of drawing the
   * coarse trace first and swapping. Capped by BRUSH_DETAIL_WAIT_MS.
   */
  onPrepareZoom?: (minMs: number, maxMs: number) => Promise<boolean> | undefined;
  /** Progressive explore: chart shell exists but series data not loaded yet. */
  awaitingData?: boolean;
  /**
   * Hold the y scale steady instead of re-ranging it on every data update.
   *
   * A one-shot load ranges once and the padded-to-data default looks right. A STREAM re-ranges on every
   * push: each new sample nudges dataMin/dataMax, so the axis and its tick labels creep continuously and
   * the trace appears to breathe rather than advance. With this on, the range is quantized to round
   * numbers and only changes when the data actually leaves it (or when it has grown wastefully wide
   * because a spike scrolled out of the window).
   */
  stickyYRange?: boolean;
  /**
   * Width of the x window, in ms, for a STREAMING chart. When set, the domain is
   * `[anchor - width, anchor]` instead of the data's own extent.
   *
   * Constant width is the point. Ranging to `[dataMin, dataMax]` means the window GROWS as samples
   * accumulate — ten seconds of data gives a ten-second axis, a minute later a sixty-second one — so the
   * whole trace is re-compressed on every push and the axis appears to rescale constantly. A fixed width
   * moves the trace right-to-left instead, and the tick spacing stays put. Mirrors LiveMapUPlotTimeSeries
   * `timeExtent`, which takes the same shape.
   */
  stickyXWindowMs?: number;
  /**
   * Right edge of that window, in epoch ms. Defaults to the newest sample when omitted.
   *
   * Supplied by the parent rather than read from the playback clock here: the parent knows whether its
   * clock is actually running, and on a live page it usually is not (see FleetTimeseries).
   * Updating it on a throttled tick — not per sample — is what keeps the motion even.
   */
  stickyXAnchorMs?: number | null;
  /**
   * Hide the red selected-time line (and its per-series value markers), and with it the drag-to-scrub
   * grab zone.
   *
   * A streaming chart animates past a playhead that means nothing on it: `selectedTime` is cross-window
   * synced and no live page drives it, so it sits wherever another page left it. Hiding the grab zone
   * matters as much as hiding the line — scrubbing calls `setSelectedTime(..., "timeseries")`, which
   * pauses playback, so leaving an invisible handle behind means a stray drag can silently stall the
   * clock for every page that does use it.
   */
  hideSelectedTimeLine?: boolean;
  /**
   * Turn off every time-navigation interaction: drag-to-brush, wheel pan, double-click reset, and the
   * selection-store writes those produce.
   *
   * Meant to be driven by "is it animating", not set once. While a live trace scrolls, brushing and
   * panning fight the moving window — the user grabs a range and the window immediately slides out from
   * under it — so the page turns them off while playing and back on when paused, where they behave
   * exactly as they do on a static chart.
   *
   * The selection writes matter independently of the ergonomics: a streaming chart has a TIME WINDOW,
   * not a selection. The window is merely what is on screen; a selected range is a stated intent the
   * rest of the app acts on — it raises the global selection banner, filters other views and offers Cut.
   * Publishing one also fed back on itself, since TimeSeries drives its zoom from `selectedRange`.
   */
  disableTimeInteractions?: boolean;
  /**
   * Zoom and pan locally without publishing anything to the selection store.
   *
   * Separate from {@link disableTimeInteractions} because the two answer different questions: that one is
   * "can the user navigate time right now", this one is "does navigating mean the app has a selection".
   * A live chart wants yes to the first while paused and no to the second always — a visible window is
   * not a stated intent, and publishing one raises the global selection banner and offers Cut over
   * something nobody selected.
   */
  suppressSelectionStore?: boolean;
  /**
   * Print each series' latest value in the series colour, in a reserved gutter at the right edge of the
   * plot, level with where that line ends.
   *
   * For pages that hide the side stats table (live timeseries collapses it to give the traces the full
   * width): the value is the one thing from the table you cannot reconstruct by eye, and at the right
   * edge it reads directly off the line it belongs to. Deliberately not on by default — with the table
   * up it is duplicate information, and the gutter is width the plot would rather have.
   *
   * The gutter is reserved through uPlot `padding`, which is read at CONSTRUCTION, so a parent toggling
   * this must also bump `visualEpoch` to remount the plots.
   */
  showEdgeValueLabels?: boolean;
}

export default function ExploreTimeSeriesUPlot(props: ExploreTimeSeriesUPlotProps) {
  let rootEl: HTMLDivElement | null = null;
  const instancesRef: { current: uPlot[] } = { current: [] };
  const instancesByIndexRef: { current: (uPlot | null)[] } = { current: [] };
  const slotWrapsRef: { current: (HTMLDivElement | null)[] } = { current: [] };
  const interactionStateRef: WeakMap<uPlot, ExploreInteractionState> = new WeakMap();
  /** Held y range per plot for `stickyYRange`. Keyed by instance, so a remount re-ranges once. */
  const stickyYRangeRef: WeakMap<uPlot, [number, number]> = new WeakMap();
  /** True while a user zoom/pan should outrank the streaming window. See syncStoresForVisibleWindow. */
  let userZoomActive = false;
  let brushRedrawRafId: number | null = null;
  let storeRedrawRafId: number | null = null;
  let legendRefreshIdleId: number | null = null;

  const refreshInstancesFlatList = () => {
    instancesRef.current = instancesByIndexRef.current.filter((u): u is uPlot => u != null);
  };

  const firstActiveInstance = (): uPlot | null => {
    for (const u of instancesByIndexRef.current) {
      if (u) {return u;}
    }
    return null;
  };

  const schedulePlotRedraw = (only?: uPlot) => {
    if (brushRedrawRafId != null) {return;}
    brushRedrawRafId = requestAnimationFrame(() => {
      brushRedrawRafId = null;
      const list = only ? [only] : instancesRef.current;
      for (const inst of list) {
        try {
          inst.redraw();
        } catch {
          /* destroyed */
        }
      }
    });
  };

  const cancelScheduledRedraws = () => {
    if (brushRedrawRafId != null) {
      cancelAnimationFrame(brushRedrawRafId);
      brushRedrawRafId = null;
    }
    if (storeRedrawRafId != null) {
      cancelAnimationFrame(storeRedrawRafId);
      storeRedrawRafId = null;
    }
    if (legendRefreshIdleId != null && typeof cancelIdleCallback !== "undefined") {
      cancelIdleCallback(legendRefreshIdleId);
      legendRefreshIdleId = null;
    }
  };

  function visibleWindowIsHeavyForLegend(): boolean {
    const dom = visibleDomainSec();
    if (!dom) {return false;}
    const visLoMs = dom[0] * 1000;
    const visHiMs = dom[1] * 1000;
    for (const bc of builtCharts()) {
      for (const sm of bc.seriesMeta) {
        const { start, end } = findSeriesIndexRangeByTime(sm.sortedData, visLoMs, visHiMs);
        if (end - start + 1 > HEAVY_LEGEND_WINDOW_POINTS) {return true;}
      }
    }
    return false;
  }

  function scheduleLegendRefresh() {
    if (legendRefreshIdleId != null && typeof cancelIdleCallback !== "undefined") {
      cancelIdleCallback(legendRefreshIdleId);
      legendRefreshIdleId = null;
    }
    const run = () => {
      legendRefreshIdleId = null;
      refreshPerChartLegends();
    };
    if (visibleWindowIsHeavyForLegend() && typeof requestIdleCallback !== "undefined") {
      legendRefreshIdleId = requestIdleCallback(run, { timeout: 400 });
    } else {
      queueMicrotask(run);
    }
  }
  const perChartLegendContainersRef: { current: (HTMLDivElement | null)[] } = { current: [] };
  const resizeObserversRef: { current: ResizeObserver[] } = { current: [] };

  /**
   * Re-fit the plots to the page width, once it has stopped changing.
   *
   * uPlot sizes its canvas in device pixels, so every `setSize` is a full re-rasterise of the plot plus
   * its axes. Resizing straight from the ResizeObserver callback re-rasterised every chart on every frame
   * of a window or split-divider drag, at widths the user was only passing through. Waiting for the width
   * to hold still spends one resize on the width actually chosen.
   *
   * Every plot is resized together rather than only the wrap that reported the change: they all take
   * their width from the same page, and one pass over a handful of instances is cheaper than tracking
   * which are individually dirty.
   *
   * Height is deliberately not part of the settle test. Wrap heights are assigned at mount from the
   * chart's own `chartHeight`, so a height change arrives as a prop-driven rebuild — but each wrap's
   * height is still read back here rather than trusting a value captured when the plot was built.
   *
   * Declared above `destroyPlots` because that function cancels the pending timer: these are `const`
   * arrow functions, so a call before this point would hit the temporal dead zone.
   */
  const WIDTH_SETTLE_MS = 1000;
  /** Ignore sub-pixel churn and scrollbar-width jitter that would re-rasterise for no visible gain. */
  const WIDTH_EPSILON_PX = 2;
  let widthSettleTimer: ReturnType<typeof setTimeout> | null = null;
  let lastResizedWidth = -1;

  const cancelWidthSettle = (): void => {
    if (widthSettleTimer !== null) {
      clearTimeout(widthSettleTimer);
      widthSettleTimer = null;
    }
  };

  const resizePlotsToCurrentWidth = (): void => {
    widthSettleTimer = null;
    const wraps = slotWrapsRef.current;
    let appliedWidth = -1;
    for (let i = 0; i < wraps.length; i++) {
      const wrap = wraps[i];
      const u = instancesByIndexRef.current[i];
      if (!wrap || !u) {continue;}
      const width = wrap.offsetWidth;
      const height = wrap.offsetHeight;
      if (width <= 0 || height <= 0) {continue;}
      try {
        u.setSize({ width: Math.max(width, 100), height });
        u.redraw();
        appliedWidth = width;
      } catch {
        /* instance destroyed between the settle and now */
      }
    }
    if (appliedWidth > 0) {
      lastResizedWidth = appliedWidth;
      debug("ExploreTimeSeriesUPlot: resized plots after width settled", { width: appliedWidth });
    }
  };

  /** Called from each wrap's ResizeObserver; the shared timer coalesces them into one resize. */
  const scheduleWidthSettleResize = (observedWidth: number): void => {
    if (observedWidth <= 0) {return;}
    if (widthSettleTimer === null && Math.abs(observedWidth - lastResizedWidth) <= WIDTH_EPSILON_PX) {
      return;
    }
    cancelWidthSettle();
    widthSettleTimer = setTimeout(resizePlotsToCurrentWidth, WIDTH_SETTLE_MS);
  };
  const fullDomainRef: { current: { min: number; max: number } } = { current: { min: 0, max: 1 } };
  /** External zoom/pan targets live here; user brush zoom updates this after uPlot settles. */
  const xVisibleRangeRef: { current: { min: number; max: number } } = { current: { min: 0, max: 1 } };
  let programmaticScale = false;
  let wheelHandler: ((e: WheelEvent) => void) | null = null;
  let dblClickHandler: ((ev: MouseEvent) => void) | null = null;
  /** Snapshot of `window.clearTimeSeriesBrush` before uPlot overrides it; restored in destroyPlots. */
  let savedClearTimeSeriesBrushSlot: { hadPrevious: boolean; fn?: () => void } | null = null;
  let bridgeRef: ExploreUPlotBridgeRefs | null = null;

  function combinedRangeMsFromStore(): [number, number] | null {
    const brush = selectedRange();
    const events = selectedRanges();
    const src =
      brush && brush.length > 0
        ? brush
        : events && events.length > 0
          ? events
          : null;
    if (!src?.length) {return null;}
    let minMs = Infinity;
    let maxMs = -Infinity;
    for (const r of src) {
      if (!r?.start_time || !r?.end_time) {continue;}
      const s = new Date(r.start_time).getTime();
      const e = new Date(r.end_time).getTime();
      if (!Number.isFinite(s) || !Number.isFinite(e)) {continue;}
      minMs = Math.min(minMs, s);
      maxMs = Math.max(maxMs, e);
    }
    if (minMs === Infinity || maxMs === -Infinity || maxMs <= minMs) {return null;}
    return [minMs, maxMs];
  }

  const [visibleDomainSec, setVisibleDomainSec] = createSignal<[number, number] | null>(null);

  const chartSlots = createMemo((): ChartSlot[] => buildChartSlots(props.charts ?? []));

  const builtCharts = createMemo((): BuiltChart[] =>
    chartSlots()
      .filter((s): s is ChartSlotPlot => s.kind === "plot")
      .map((s) => s.built),
  );

  /**
   * The in-chart stats legend belongs to the stacked explore layout, where there is no side table.
   * Fleet layout renders a per-boat LegendTable next to every chart carrying the same Min/Max/Avg/Std,
   * so drawing it inside as well is duplicate information — and with one line per boat it covers the
   * plot. Fleet suppresses it entirely.
   */
  const showInChartLegend = () => props.layout !== "fleet";

  function refreshPerChartLegends() {
    if (!showInChartLegend()) {return;}
    const slots = chartSlots();
    const containers = perChartLegendContainersRef.current;
    const dom = visibleDomainSec();
    // [Sel] means "at the playhead", but a streaming chart hides the playhead and has no selected
    // instant — selectedTime just sits wherever another page left it. There the newest sample is the
    // meaningful reading, and the window anchor already is that (frozen on pause, so [Sel] matches the
    // still window). Mirrors legendSelTime in TimeSeries so both legends agree.
    const anchorMs = props.stickyXAnchorMs;
    const t =
      props.hideSelectedTimeLine && typeof anchorMs === "number" && Number.isFinite(anchorMs)
        ? new Date(anchorMs)
        : selectedTime();
    const selMs = t instanceof Date && Number.isFinite(t.getTime()) ? t.getTime() : null;

    containers.forEach((container) => {
      container?.replaceChildren();
    });

    if (!dom) {return;}
    const [loS, hiS] = dom;
    const visLoMs = loS * 1000;
    const visHiMs = hiS * 1000;

    for (const slot of slots) {
      if (slot.kind !== "plot") {continue;}
      const bc = slot.built;
      const container = containers[slot.index];
      if (!container) {continue;}

      let pad = 0;
      for (const sm of bc.seriesMeta) {
        pad = Math.max(pad, sm.label.length);
      }
      pad += 3;

      for (const sm of bc.seriesMeta) {
        const st = statsForSeries(sm.sortedData, visLoMs, visHiMs, selMs, sm.lineType);
        const padded = sm.label.length + 3 !== pad ? sm.label.padEnd(pad, "\u00A0") : sm.label;
        // The rate on screen, read off the samples (explore level of detail splices finer windows into 1 Hz).
        const rate = sampleRateLegendBracket(medianSampleIntervalMs(sm.sortedData, visLoMs, visHiMs, pointMs));
        const metricsText = `${st.sel !== undefined ? ` [Sel: ${formatStat(st.sel)}]` : ""} [Min: ${formatStat(st.min)}] [Max: ${formatStat(st.max)}] [Avg: ${formatStat(st.avg)}] [Std: ${formatStat(st.std)}]${rate}${lineTypeLegendBracket(sm.lineType)}`;

        const row = document.createElement("div");
        row.className = "explore-uplot-legend-line";
        row.textContent = `${padded}${metricsText}`;
        row.style.setProperty("color", sm.color);
        container.appendChild(row);
      }
    }
  }

  function restoreClearTimeSeriesBrushSlot() {
    if (savedClearTimeSeriesBrushSlot === null) {return;}
    const slot = savedClearTimeSeriesBrushSlot;
    savedClearTimeSeriesBrushSlot = null;
    if (slot.hadPrevious && slot.fn) {
      window.clearTimeSeriesBrush = slot.fn;
    } else {
      delete (window as Window & { clearTimeSeriesBrush?: () => void }).clearTimeSeriesBrush;
    }
  }

  function destroyPlots() {
    cancelScheduledRedraws();
    // A pending settle would otherwise fire against destroyed instances / detached wraps.
    cancelWidthSettle();
    lastResizedWidth = -1;
    restoreClearTimeSeriesBrushSlot();
    const interactHost = props.layout === "fleet" ? props.getHostElement() : rootEl;
    if (wheelHandler && interactHost) {
      interactHost.removeEventListener("wheel", wheelHandler, { capture: true });
      wheelHandler = null;
    }
    if (dblClickHandler && interactHost) {
      interactHost.removeEventListener("dblclick", dblClickHandler, { capture: true });
      dblClickHandler = null;
    }
    for (const ro of resizeObserversRef.current) {
      try {
        ro.disconnect();
      } catch {
        /* noop */
      }
    }
    resizeObserversRef.current = [];
    for (const u of instancesRef.current) {
      try {
        u.destroy();
      } catch {
        /* noop */
      }
    }
    instancesRef.current = [];
    instancesByIndexRef.current = [];
    slotWrapsRef.current = [];
    perChartLegendContainersRef.current = [];
    const host = props.getHostElement();
    if (props.layout === "fleet" && host) {
      host.querySelectorAll(".time-series-single-chart .explore-uplot-chart-wrap").forEach((el) => el.remove());
    } else if (rootEl) {
      rootEl.querySelectorAll(".explore-uplot-chart-wrap").forEach((el) => el.remove());
    }
  }

  /**
   * While a brush zoom waits for its finer data, data syncs are HELD (not drawn): drawn early, the finer
   * window shows up on the still-unzoomed full timeline. They are applied in the same task as the zoom.
   */
  let brushZoomPending = false;
  let deferredDataSync = false;

  function emitVisibleRange(minSec: number, maxSec: number, phase: "live" | "settled") {
    if (!props.onVisibleRangeChange) {return;}
    // A brush zoom is waiting for its finer data: the brushed window is the target until it lands. Any
    // scale event meanwhile still describes the old (full) view, and reporting it made the parent drop
    // the windows it was loading for the brush — they then only appeared after the zoom, as a swap.
    if (brushZoomPending) {return;}
    // `fullView`: back on everything the chart shows (reset, or zoomed out to it). The chart's full range
    // is where the series have values, which can be narrower than the page's loaded rows.
    const full = fullDomainRef.current;
    const fullView = full.max > full.min && maxSec - minSec >= (full.max - full.min) * LOD_FULL_VIEW_FRACTION;
    props.onVisibleRangeChange(minSec * 1000, maxSec * 1000, phase, fullView);
  }

  /** Sync selection store from visible x window (D3 TimeSeries parity). */
  function syncStoresForVisibleWindow(minSec: number, maxSec: number, kind: "zoom" | "pan" | "reset") {
    emitVisibleRange(minSec, maxSec, "settled");
    const full = fullDomainRef.current;
    const span = maxSec - minSec;
    const fullSpan = full.max - full.min;
    const tol = Math.max(1e-3, fullSpan * 0.002);
    const atFull =
      kind === "reset" ||
      fullSpan <= 0 ||
      (Math.abs(minSec - full.min) <= tol && Math.abs(maxSec - full.max) <= tol) ||
      span >= fullSpan - tol;

    // Remembered because `programmaticScale` only lives for the setScale call (a microtask clears it), so
    // it cannot hold a zoom across the next redraw — and a paused live chart still redraws every tick as
    // samples arrive. On the dataset page `selectedRange` is what makes a zoom stick; a chart that does
    // not publish one needs to keep it here instead.
    userZoomActive = !atFull;

    if (props.suppressSelectionStore) {
      setVisibleDomainSec([minSec, maxSec]);
      props.onZoomedChange(!atFull);
      return;
    }

    if (atFull) {
      // Only explicit reset (double-click / resetZoomToFull) clears map brush from the store.
      // When the chart's loaded domain is already the brush span, visible === full is expected
      // and must not wipe selectedRange (FleetTimeSeries + map brush).
      if (kind === "reset") {
        if (!isMapBrushProtected()) {
          if (selectedRange().length > 0) {
            setSelectedRange([]);
          }
          if (hasSelection()) {
            setHasSelection(false);
          }
          props.onZoomedChange(false);
        } else {
          props.onZoomedChange(selectedRange().length > 0);
        }
      } else {
        props.onZoomedChange(selectedRange().length > 0);
      }
    } else if (isMapBrushProtected()) {
      setVisibleDomainSec([minSec, maxSec]);
      props.onZoomedChange(true);
    } else {
      const t0 = new Date(minSec * 1000);
      const t1 = new Date(maxSec * 1000);
      setSelectedRange([{ start_time: t0.toISOString(), end_time: t1.toISOString() }]);
      setHasSelection(true);
      props.onZoomedChange(true);
      if (kind === "zoom") {
        if (requestTimeControl("timeseries")) {
          setIsManualTimeChange(true);
          setSelectedTime(t0, "timeseries");
        }
      } else if (kind === "pan") {
        const cur = selectedTime();
        if (cur instanceof Date && Number.isFinite(cur.getTime()) && (cur < t0 || cur > t1) && requestTimeControl("timeseries")) {
          setIsManualTimeChange(true);
          setSelectedTime(t0, "timeseries");
        }
      }
    }
    setVisibleDomainSec([minSec, maxSec]);
  }

  /**
   * Slide the streaming window to a new anchor, for SMOOTH scrolling between data arrivals.
   *
   * Separate from {@link applyXAll} on purpose: that one clamps to the data's own full extent, which
   * is right for zoom/reset but wrong here — a smooth anchor deliberately runs slightly PAST the
   * newest sample (the parent extrapolates with wall-clock so the trace glides instead of stepping
   * once per server flush). Clamping would snap it straight back to the last sample and undo it.
   *
   * Kept as cheap as it can be: setScale on each plot and nothing else. In particular it does NOT
   * touch `visibleDomainSec` — that drives the legend stats, which are recomputed on the parent's
   * data tick anyway, and bumping a signal per animation frame is exactly the churn this path exists
   * to avoid.
   */
  function applyStreamingAnchor(anchorMs: number): void {
    const windowMs = Number(props.stickyXWindowMs);
    if (!Number.isFinite(windowMs) || windowMs <= 0) {return;}
    const maxSec = anchorMs / 1000;
    const minSec = maxSec - windowMs / 1000;
    if (!(maxSec > minSec)) {return;}

    xVisibleRangeRef.current = { min: minSec, max: maxSec };
    programmaticScale = true;
    try {
      for (const u of instancesRef.current) {
        try {
          u.setScale("x", { min: minSec, max: maxSec });
        } catch {
          /* instance destroyed mid-frame */
        }
      }
    } finally {
      queueMicrotask(() => {
        programmaticScale = false;
      });
    }
  }

  // Drive the window off the anchor. Only while animating — paused, the user's zoom/pan owns the
  // scale and re-applying the window under them would fight every drag.
  createEffect(
    on(
      () => props.stickyXAnchorMs,
      (anchorMs) => {
        if (typeof anchorMs !== "number" || !Number.isFinite(anchorMs)) {return;}
        if (!props.disableTimeInteractions) {return;}
        applyStreamingAnchor(anchorMs);
      },
      { defer: true },
    ),
  );

  function applyXAll(minSec: number, maxSec: number) {
    const full = fullDomainRef.current;
    let mn = minSec;
    let mx = maxSec;
    if (!(Number.isFinite(mn) && Number.isFinite(mx) && mx > mn)) {
      mn = full.min;
      mx = full.max;
    } else {
      mn = Math.max(mn, full.min);
      mx = Math.min(mx, full.max);
      if (!(mx > mn)) {
        mn = full.min;
        mx = full.max;
      }
    }
    xVisibleRangeRef.current = { min: mn, max: mx };
    programmaticScale = true;
    try {
      for (const u of instancesRef.current) {
        u.setScale("x", { min: mn, max: mx });
      }
      setVisibleDomainSec([mn, mx]);
    } finally {
      queueMicrotask(() => {
        programmaticScale = false;
      });
    }
  }

  function clearAllBrushes() {
    for (const u of instancesRef.current) {
      const state = interactionStateRef.get(u);
      if (state) {
        state.brushLeft = null;
        state.brushWidth = null;
      }
      try {
        u.redraw();
      } catch {
        /* noop */
      }
    }
  }

  function mountPlots() {
    destroyPlots();
    const host = props.getHostElement();
    if (!rootEl || !host) {return;}

    const fleetMode = props.layout === "fleet";
    rootEl.classList.toggle("explore-uplot-root--fleet-host", fleetMode);
    if (fleetMode) {
      rootEl.style.display = "none";
    } else {
      rootEl.style.display = "";
    }

    const slots = chartSlots();
    if (slots.length === 0) {
      props.onBridgeReady(null);
      return;
    }

    const plotSlots = slots.filter((s): s is ChartSlotPlot => s.kind === "plot");
    const fleetContainers = fleetMode
      ? Array.from(host.querySelectorAll<HTMLElement>(".time-series-single-chart"))
      : [];

    perChartLegendContainersRef.current = slots.map(() => null);
    instancesByIndexRef.current = slots.map(() => null);
    slotWrapsRef.current = slots.map(() => null);

    slots.forEach((slot) => {
      const mountParent = fleetMode ? fleetContainers[slot.index] ?? null : rootEl;
      if (!mountParent) {return;}

      const wrap = document.createElement("div");
      wrap.dataset.chartIndex = String(slot.index);
      slotWrapsRef.current[slot.index] = wrap;

      if (slot.kind === "placeholder") {
        if (fleetMode) {
          mountParent.replaceChildren();
          mountParent.appendChild(wrap);
        } else {
          rootEl?.appendChild(wrap);
        }
        mountPlaceholderSlot(wrap, slot);
        return;
      }

      if (fleetMode) {
        mountParent.replaceChildren();
        mountParent.appendChild(wrap);
      } else {
        rootEl?.appendChild(wrap);
      }

      mountPlotSlot(wrap, slot, host, props.timezone);
    });

    refreshInstancesFlatList();

    if (plotSlots.length === 0) {
      props.onBridgeReady(null);
      return;
    }

    const globalMin = Math.min(...plotSlots.map((s) => s.built.fullMinSec));
    const globalMax = Math.max(...plotSlots.map((s) => s.built.fullMaxSec));
    fullDomainRef.current = { min: globalMin, max: globalMax };
    xVisibleRangeRef.current = { min: globalMin, max: globalMax };

    finalizePlotMount(globalMin, globalMax, host);
  }

  function mountPlotSlot(
    wrap: HTMLDivElement,
    slot: ChartSlotPlot,
    host: HTMLElement,
    tz: string | null,
  ): uPlot | null {
    const bc = slot.built;
    wrap.className = "explore-uplot-chart-wrap";
    wrap.dataset.chartIndex = String(slot.index);
    wrap.replaceChildren();

    const legendInner = document.createElement("div");
    legendInner.className = "explore-uplot-chart-legend-inner";
    legendInner.setAttribute("aria-hidden", "true");

    const chartWidth = Math.max(wrap.offsetWidth || host.offsetWidth || 400, 100) - MARGIN.left - MARGIN.right;
    const chartPlotHeight = bc.chartHeight;
    const chartSlotHeight = chartPlotHeight + MARGIN.top + MARGIN.bottom;
    wrap.style.height = `${chartSlotHeight}px`;
    wrap.style.minHeight = `${chartSlotHeight}px`;
    wrap.style.flexShrink = "0";

    const opts = buildUPlotOptions(bc, chartWidth, chartSlotHeight, tz);
    try {
      const u = new uPlot(opts, bc.data, wrap);
      if (showInChartLegend()) {
        wrap.appendChild(legendInner);
        perChartLegendContainersRef.current[slot.index] = legendInner;
      } else {
        perChartLegendContainersRef.current[slot.index] = null;
      }
      instancesByIndexRef.current[slot.index] = u;
      // The plot was just built at this width, and ResizeObserver delivers an observation as soon as it
      // starts observing — without this seed that first callback would schedule a resize to the size the
      // chart already has.
      if (wrap.offsetWidth > 0) {lastResizedWidth = wrap.offsetWidth;}
      const ro = new ResizeObserver(() => {
        scheduleWidthSettleResize(wrap.offsetWidth);
      });
      ro.observe(wrap);
      resizeObserversRef.current.push(ro);
      return u;
    } catch (e) {
      logError("ExploreTimeSeriesUPlot: uPlot create error", e);
      return null;
    }
  }

  function buildUPlotOptions(
    bc: BuiltChart,
    chartWidth: number,
    chartSlotHeight: number,
    tz: string | null,
  ): uPlot.Options {
    /**
     * Distinct channel names for the in-plot caption, or empty when the stacked layout's stats legend
     * already carries them. Resolved at construction, like `padding` below: the layout only changes when
     * the parent re-mounts the plots anyway.
     */
    const channelLabels = showInChartLegend()
      ? []
      : [...new Set(bc.seriesMeta.map((m) => channelNameFromLabel(m.label)))].filter(Boolean);
    return {
        width: Math.max(chartWidth + MARGIN.left + MARGIN.right, 100),
        height: chartSlotHeight,
        // Off, this falls through to uPlot's own autoPadSide, which computes 0 on the right for this axis
        // configuration (x on side 2, y on side 3) — so not setting it is pixel-identical to before.
        padding: props.showEdgeValueLabels
          ? ([null, EDGE_LABEL_GUTTER_PX, null, null] as uPlot.Padding)
          : undefined,
        series: [
          { label: "Time" },
          ...bc.seriesMeta.map((m) => ({
            label: m.label,
            stroke: m.color,
            scale: "y",
            width: 1,
            spanGaps: true,
            points: { show: showSampleDots, size: SAMPLE_DOT_SIZE_PX, width: 0, fill: m.color, stroke: m.color },
            value: (_u: uPlot, raw: number | null) => (raw == null ? "—" : String(Number(raw).toFixed(2))),
          })),
        ],
        scales: {
          x: {
            time: true,
            range: (_u, dataMin, dataMax) => {
              const full = fullDomainRef.current;
              const lo = typeof dataMin === "number" && Number.isFinite(dataMin) ? dataMin : full.min;
              const hi = typeof dataMax === "number" && Number.isFinite(dataMax) ? dataMax : full.max;

              /**
               * Streaming window: constant width, right edge at the anchor, so the trace scrolls.
               * Scale units are SECONDS here (x is built as ts/1000), hence the /1000.
               */
              const windowSec =
                typeof props.stickyXWindowMs === "number" && props.stickyXWindowMs > 0
                  ? props.stickyXWindowMs / 1000
                  : 0;
              const streamingWindow = (): [number, number] | null => {
                if (windowSec <= 0) {return null;}
                const anchorMs = props.stickyXAnchorMs;
                const anchorSec =
                  typeof anchorMs === "number" && Number.isFinite(anchorMs)
                    ? anchorMs / 1000
                    : Number.isFinite(hi)
                      ? hi
                      : null;
                return anchorSec === null ? null : [anchorSec - windowSec, anchorSec];
              };

              // While ANIMATING the streaming window is authoritative and outranks any zoom. This order
              // matters: a stale cross-window brush selection used to drive the domain through the zoom
              // branch, so the live window never applied and the axis re-ranged on every push.
              // LiveMapUPlotTimeSeries takes the same position — "brush selection does NOT affect this".
              if (props.disableTimeInteractions) {
                const win = streamingWindow();
                if (win) {return win;}
              }

              // Paused, a zoom or pan wins, so brushing behaves as it does on a static chart.
              // `programmaticScale` only survives the setScale call itself, so `userZoomActive` is what
              // holds the zoom across the redraws that keep arriving while paused.
              if (programmaticScale || userZoomActive) {
                const r = xVisibleRangeRef.current;
                if (Number.isFinite(r.min) && Number.isFinite(r.max) && r.max > r.min) {
                  return [r.min, r.max];
                }
              }

              // Paused and un-zoomed: still the streaming window, so pausing does not jump the view to
              // the whole retained buffer.
              const win = streamingWindow();
              if (win) {return win;}

              // Un-zoomed: where the series have values, not the whole timeline (all-null rows at either
              // end — see occupiedXRangeSec). fullDomainRef is that range, refreshed on every data sync.
              const occLo = Number.isFinite(full.min) ? Math.max(lo, full.min) : lo;
              const occHi = Number.isFinite(full.max) ? Math.min(hi, full.max) : hi;
              if (occHi > occLo) {return [occLo, occHi];}
              return hi > lo ? [lo, hi] : [lo, lo + 60];
            },
          },
          y: {
            range: (u, dataMin, dataMax) => {
              const lo = dataMin ?? 0;
              const hi = dataMax ?? 1;
              const span = hi - lo;
              const basePad = span !== 0 ? Math.abs(span) : Math.max(Math.abs(lo), Math.abs(hi), 1);
              const padded: [number, number] = [
                lo - (basePad * 0.05 || 1),
                hi + (basePad * 0.25 || 1),
              ];
              if (!props.stickyYRange) {return padded;}

              // Streaming: keep the range we already show while the data still fits inside it, so the
              // axis steps between round values instead of drifting with every sample.
              const next = resolveStickyYRange(stickyYRangeRef.get(u), lo, hi, padded);
              stickyYRangeRef.set(u, next);
              return next;
            },
          },
        },
        axes: [
          {
            stroke: "#ffffff",
            grid: { show: false },
            ticks: { show: true, stroke: "#ffffff", width: 1 },
            font: "12px sans-serif",
            // Up to a week: uPlot draws no ticks at all when no step fits the span, so the list must
            // reach past any span the chart can show.
            incrs: [
              0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800,
              3600, 7200, 10800, 21600, 43200, 86400, 172800, 604800,
            ],
            // "15:07:56.125" is wider than "15:07:56": more room between ticks once they go sub-second.
            space: (_u: uPlot, _axisIdx: number, mn: number, mx: number) => (mx - mn < 30 ? 110 : 80),
            side: 2,
            size: 40,
            // Sub-second steps (zoomed into a 10 Hz / 100 Hz window) print as many decimals as the step needs.
            values: (_u, vals, _axisIdx, _space, incr) => {
              const step = Number(incr);
              if (step >= 86400) {return vals.map((v) => formatAxisDate(v, tz));}
              const frac = !(step < 1) ? 0 : step >= 0.1 ? 1 : step >= 0.01 ? 2 : 3;
              return vals.map((v) => formatAxisTime(v, tz, frac));
            },
          },
          {
            stroke: "#ffffff",
            grid: { show: false },
            ticks: { show: true, stroke: "#ffffff", width: 1 },
            font: "12px sans-serif",
            space: 50,
            side: 3,
            size: 44,
          },
        ],
        cursor: {
          x: false,
          y: false,
          points: { show: false },
          drag: { x: false, y: false, dist: 5, uni: 12, setScale: false },
        },
        select: { show: true, left: 0, top: 0, width: 0, height: 0 },
        legend: { show: false },
        hooks: {
          setScale: [
            (u, key) => {
              if (key !== "x") {return;}
              const mn = u.scales.x.min;
              const mx = u.scales.x.max;
              if (mn == null || mx == null || !Number.isFinite(mn) || !Number.isFinite(mx) || !(mx > mn)) {return;}
              xVisibleRangeRef.current = { min: mn, max: mx };
              // Before the programmatic guard: a zoom applied from the selection store (map brush) must
              // drive the parent's level of detail as much as a wheel zoom does.
              emitVisibleRange(mn, mx, "live");
              if (programmaticScale) {return;}

              const prev = visibleDomainSec() ?? [fullDomainRef.current.min, fullDomainRef.current.max];
              const prevSpan = prev[1] - prev[0];
              const nextSpan = mx - mn;
              const tol = Math.max(1e-3, prevSpan * 0.002);
              const kind: "zoom" | "pan" = Math.abs(nextSpan - prevSpan) <= tol ? "pan" : "zoom";

              programmaticScale = true;
              try {
                for (const inst of instancesRef.current) {
                  if (inst === u) {continue;}
                  inst.setScale("x", { min: mn, max: mx });
                }
              } finally {
                queueMicrotask(() => {
                  programmaticScale = false;
                });
              }

              syncStoresForVisibleWindow(mn, mx, kind);
            },
          ],
          draw: [
            /**
             * Channel name, upper left, in white.
             *
             * The only thing that identifies a FLEET plot: its lines are all the same channel and are
             * coloured by boat, the side stats table names boats rather than channels, and collapsing that
             * table leaves nothing on screen naming the channel at all. Ports the D3 renderer's
             * `.chart-channel-label` (TimeSeries.tsx, fleet branch) and the equivalent draw hook in
             * PlaybackTimeSeries, so all three renderers agree.
             *
             * Suppressed in the stacked layout, where the in-chart stats legend already leads with the
             * channel name in the series colour and a second copy would sit on top of it.
             */
            (u) => {
              if (channelLabels.length === 0) {return;}

              // u.bbox is in canvas device px, so every offset and the font have to be scaled the way the
              // playhead and edge labels below are — unscaled values render tiny on a HiDPI display.
              const ctx = u.ctx;
              const { left, top } = u.bbox;
              const pxRatio = plotPxRatio(u);
              ctx.save();
              ctx.fillStyle = "#fff";
              ctx.font = `${Math.round(CHANNEL_LABEL_FONT_PX * pxRatio)}px sans-serif`;
              ctx.textAlign = "left";
              ctx.textBaseline = "top";
              channelLabels.forEach((name, i) => {
                ctx.fillText(
                  name,
                  Math.round(left + CHANNEL_LABEL_LEFT_PX * pxRatio),
                  Math.round(top + (CHANNEL_LABEL_TOP_PX + i * CHANNEL_LABEL_LINE_PX) * pxRatio),
                );
              });
              ctx.restore();
            },
            (u) => {
              const ctx = u.ctx;
              const { left, top, width, height } = u.bbox;
              ctx.save();
              ctx.strokeStyle = "#fff";
              ctx.lineWidth = 1;
              ctx.setLineDash([]);
              ctx.beginPath();
              ctx.moveTo(left, top);
              ctx.lineTo(left, top + height);
              ctx.moveTo(left, top + height);
              ctx.lineTo(left + width, top + height);
              ctx.stroke();
              ctx.restore();
            },
            (u) => {
              const state = interactionStateRef.get(u);
              if (!state) {return;}
              const { top, height, left } = u.bbox;
              const { ctx } = u;
              if ((state.brushWidth ?? 0) > 0 && state.brushLeft != null) {
                ctx.save();
                ctx.fillStyle = "rgba(211, 211, 211, 0.28)";
                ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
                ctx.lineWidth = 1;
                ctx.fillRect(left + state.brushLeft, top, state.brushWidth ?? 0, height);
                ctx.strokeRect(left + state.brushLeft, top, state.brushWidth ?? 0, height);
                ctx.restore();
              }
              if (state.hoverLeft != null) {
                ctx.save();
                ctx.strokeStyle = "rgba(255,255,255,0.95)";
                ctx.lineWidth = 1;
                ctx.setLineDash([]);
                ctx.beginPath();
                ctx.moveTo(left + state.hoverLeft, top);
                ctx.lineTo(left + state.hoverLeft, top + height);
                ctx.stroke();
                ctx.restore();
              }
            },
            (u) => {
              const interaction = interactionStateRef.get(u);
              if (interaction?.brushDragging) {return;}
              // Streaming: no playhead line, circles or value labels — see hideSelectedTimeLine.
              if (props.hideSelectedTimeLine) {return;}

              const st = selectedTime();
              if (!(st instanceof Date) || !Number.isFinite(st.getTime())) {return;}
              const sec = st.getTime() / 1000;
              const x0 = plotXFromTimeSec(u, sec);
              if (!Number.isFinite(x0)) {return;}
              const { ctx } = u;
              const { top, height, left, width } = u.bbox;

              // Red selected-time vertical line
              ctx.save();
              ctx.strokeStyle = "rgba(220,50,50,0.95)";
              ctx.lineWidth = 2;
              ctx.setLineDash([]);
              ctx.beginPath();
              ctx.moveTo(x0, top);
              ctx.lineTo(x0, top + height);
              ctx.stroke();
              ctx.restore();

              // Circles + labels at selected time for each series (canvas px + u.pxRatio, like uPlot axes)
              const pxRatio = plotPxRatio(u);
              const plotRight = left + width;
              const selMs = st.getTime();
              const radius = 4 * pxRatio;
              const fontSize = Math.round(11 * pxRatio);
              const labelOffsetX = 34 * pxRatio; // gap to the right of playhead when drawing on the right
              const labelOffsetXFlip = 10 * pxRatio; // tighter gap when flipped left (full labelOffsetX was too far)
              const labelOffsetY = 10 * pxRatio; // vertical clearance above the point (half of prior 20)

              type SelLabel = { yPos: number; text: string; color: string };
              const selLabels: SelLabel[] = [];

              bc.seriesMeta.forEach((sm) => {
                const yVal = closestY(sm.sortedData, selMs, ALIGN_THRESHOLD_MS, sm.lineType);
                if (yVal == null) {return;}
                const yPos = u.valToPos(yVal, "y", true);
                if (!Number.isFinite(yPos) || yPos < top || yPos > top + height) {return;}

                // Circle
                ctx.save();
                ctx.beginPath();
                ctx.arc(x0, yPos, radius, 0, Math.PI * 2);
                ctx.fillStyle = sm.color;
                ctx.fill();
                ctx.strokeStyle = "#fff";
                ctx.lineWidth = pxRatio;
                ctx.stroke();
                ctx.restore();

                selLabels.push({ yPos, text: formatStat(yVal), color: sm.color });
              });

              // One font setup for all labels (avoids ctx.restore() resetting font between series; crisp grid snap)
              const labelFont = `bold ${fontSize}px system-ui, "Segoe UI", sans-serif`;
              ctx.save();
              ctx.font = labelFont;
              ctx.textBaseline = "alphabetic";
              ctx.textAlign = "left";
              for (const { yPos, text: label, color } of selLabels) {
                ctx.fillStyle = color;
                const textWidth = ctx.measureText(label).width;
                let labelX = x0 + labelOffsetX;
                if (labelX + textWidth > plotRight) {
                  labelX = Math.max(left, x0 - labelOffsetXFlip - textWidth);
                }
                const textY = yPos - labelOffsetY;
                ctx.fillText(label, Math.round(labelX), Math.round(textY));
              }
              ctx.restore();
            },
            /**
             * Latest value per series, printed in the gutter reserved by `padding` above. Stands in for
             * the side stats table on pages that collapse it — see `showEdgeValueLabels`.
             */
            (u) => {
              if (!props.showEdgeValueLabels) {return;}
              const { ctx } = u;
              const { top, height, left, width } = u.bbox;

              // The right edge of the VISIBLE domain, not the playhead. The labels sit at that edge, so
              // that is the instant they have to report — and while streaming it is the anchor anyway, so
              // this reads the same as the table's SEL column. Paused, the playhead draws its own value
              // labels (the hook above); these stay the latest-value column and do not duplicate them.
              const edgeMs = (u.scales.x.max ?? 0) * 1000;
              if (!Number.isFinite(edgeMs) || edgeMs <= 0) {return;}

              const pxRatio = plotPxRatio(u);
              const fontSize = Math.round(EDGE_LABEL_FONT_PX * pxRatio);
              const bottom = top + height;

              type EdgeLabel = { yPos: number; text: string; color: string };
              const labels: EdgeLabel[] = [];
              for (const sm of bc.seriesMeta) {
                const yVal = closestY(sm.sortedData, edgeMs, ALIGN_THRESHOLD_MS, sm.lineType);
                if (yVal == null) {continue;}
                const yPos = u.valToPos(yVal, "y", true);
                if (!Number.isFinite(yPos) || yPos < top || yPos > bottom) {continue;}
                labels.push({ yPos, text: formatStat(yVal), color: sm.color });
              }
              if (labels.length === 0) {return;}

              // De-overlap. Lines converge constantly on these charts (that is what makes them worth
              // watching), and stacked-up labels are unreadable — so push down through the sorted list,
              // then correct back up off the floor. The label no longer sits exactly on its line, which
              // is why the colour has to carry the identity.
              labels.sort((a, b) => a.yPos - b.yPos);
              const lineH = fontSize * 1.15;
              let prevY = -Infinity;
              for (const label of labels) {
                if (label.yPos - prevY < lineH) {label.yPos = prevY + lineH;}
                prevY = label.yPos;
              }
              let floor = bottom;
              for (let i = labels.length - 1; i >= 0; i--) {
                const label = labels[i];
                if (!label) {continue;}
                if (label.yPos > floor) {label.yPos = floor;}
                floor = label.yPos - lineH;
              }

              ctx.save();
              ctx.font = `bold ${fontSize}px system-ui, "Segoe UI", sans-serif`;
              ctx.textBaseline = "middle";
              ctx.textAlign = "left";
              const labelX = Math.round(left + width + EDGE_LABEL_GAP_PX * pxRatio);
              for (const { yPos, text, color } of labels) {
                ctx.fillStyle = color;
                ctx.fillText(text, labelX, Math.round(Math.max(top + fontSize / 2, yPos)));
              }
              ctx.restore();
            },
          ],
          ready: [
            (u) => {
              const over = u.root.querySelector(".u-over") as HTMLElement | null;
              if (!over) {return;}
              const interactionState: ExploreInteractionState = {
                hoverLeft: null,
                brushLeft: null,
                brushWidth: null,
                brushDragging: false,
              };
              interactionStateRef.set(u, interactionState);
              let activePointerId: number | null = null;
              let dragMoved = false;
              let dragStartClient: { x: number; y: number } | null = null;
              let dragStartLeft: number | null = null;
              // True while the user has grabbed the red selected-time line and is scrubbing it.
              let scrubbingPlayhead = false;

              /** Plot-relative canvas x of the red selected-time line, or null when not drawable. */
              const playheadPlotX = (): number | null => {
                const st = selectedTime();
                if (!(st instanceof Date) || !Number.isFinite(st.getTime())) {return null;}
                const abs = plotXFromTimeSec(u, st.getTime() / 1000);
                if (!Number.isFinite(abs)) {return null;}
                return abs - u.bbox.left;
              };

              /** True when plot-relative canvas x `left` is within grab range of the red line. */
              const nearPlayhead = (left: number): boolean => {
                // Nothing drawn, nothing to grab — an invisible handle here would let a stray drag scrub
                // (and so pause playback) on a page that never shows a playhead.
                if (props.hideSelectedTimeLine) {return false;}
                const px = playheadPlotX();
                if (px == null) {return false;}
                return Math.abs(left - px) <= PLAYHEAD_GRAB_PX * plotPxRatio(u);
              };

              const syncCursorToPointer = (clientX: number) => {
                const left = correctedPointerPlotX(over, clientX, u.bbox.width, u);
                interactionState.hoverLeft = left;
                return left;
              };

              const scrubToPointer = (left: number) => {
                const tSec = u.posToVal(u.bbox.left + left, "x", true);
                if (Number.isFinite(tSec)) {
                  setSelectedTime(new Date(tSec * 1000), "timeseries");
                }
              };

              const clearSelection = () => {
                interactionState.brushLeft = null;
                interactionState.brushWidth = null;
              };

              over.addEventListener("pointerdown", (ev: Event) => {
                const pe = ev as PointerEvent;
                // A new gesture supersedes a pending brush zoom: draw whatever data it was holding.
                pendingBrushZoomToken++;
                if (brushZoomPending) {releaseHeldDataSync();}
                activePointerId = pe.pointerId;
                dragStartClient = { x: pe.clientX, y: pe.clientY };
                dragStartLeft = syncCursorToPointer(pe.clientX);
                dragMoved = false;
                // Grabbing the red selected-time line starts a scrub instead of a brush-zoom.
                scrubbingPlayhead =
                  nearPlayhead(dragStartLeft) && requestTimeControl("timeseries");
                if (scrubbingPlayhead) {
                  setIsManualTimeChange(true);
                }
                // While scrubbing, keep drawing the red line so it tracks the pointer.
                // Streaming charts never brush: there is nothing to select on a moving window, and a
                // half-drawn brush rectangle would linger over the trace.
                interactionState.brushDragging = !scrubbingPlayhead && !props.disableTimeInteractions;
                clearSelection();
                schedulePlotRedraw(u);
                try {
                  over.setPointerCapture(pe.pointerId);
                } catch {
                  /* noop */
                }
              });
              over.addEventListener("pointermove", (ev: Event) => {
                const pe = ev as PointerEvent;
                const left = syncCursorToPointer(pe.clientX);
                // Affordance: show a horizontal-resize cursor over/while dragging the red line.
                if (activePointerId == null || scrubbingPlayhead) {
                  over.style.cursor = scrubbingPlayhead || nearPlayhead(left) ? "ew-resize" : "";
                }
                if (activePointerId !== pe.pointerId || !dragStartClient || dragStartLeft == null) {return;}
                if (scrubbingPlayhead) {
                  scrubToPointer(left);
                  schedulePlotRedraw(u);
                  return;
                }
                if (Math.abs(pe.clientX - dragStartClient.x) > 4 || Math.abs(pe.clientY - dragStartClient.y) > 4) {
                  dragMoved = true;
                }
                if (dragMoved && !props.disableTimeInteractions) {
                  interactionState.brushLeft = Math.min(dragStartLeft, left);
                  interactionState.brushWidth = Math.abs(left - dragStartLeft);
                } else {
                  clearSelection();
                }
                schedulePlotRedraw(u);
              });
              over.addEventListener("pointerup", (ev: Event) => {
                const pe = ev as PointerEvent;
                if (activePointerId !== pe.pointerId) {return;}
                interactionState.brushDragging = false;
                const left = syncCursorToPointer(pe.clientX);
                const startClient = dragStartClient;
                const startLeft = dragStartLeft;
                const wasScrubbing = scrubbingPlayhead;
                scrubbingPlayhead = false;
                activePointerId = null;
                dragStartClient = null;
                dragStartLeft = null;
                try {
                  over.releasePointerCapture(pe.pointerId);
                } catch {
                  /* noop */
                }
                if (wasScrubbing) {
                  scrubToPointer(left);
                  dragMoved = false;
                  clearSelection();
                  schedulePlotRedraw(u);
                  return;
                }
                if (!startClient || startLeft == null) {
                  dragMoved = false;
                  clearSelection();
                  return;
                }
                const dx = Math.abs(pe.clientX - startClient.x);
                if (dragMoved && dx > 6 && !props.disableTimeInteractions) {
                  const minLeft = Math.min(startLeft, left);
                  const maxLeft = Math.max(startLeft, left);
                  const minSec = maxLeft - minLeft > 1 ? u.posToVal(u.bbox.left + minLeft, "x", true) : Number.NaN;
                  const maxSec = maxLeft - minLeft > 1 ? u.posToVal(u.bbox.left + maxLeft, "x", true) : Number.NaN;
                  if (Number.isFinite(minSec) && Number.isFinite(maxSec) && maxSec > minSec) {
                    const commit = () => {
                      clearAllBrushes();
                      applyXAll(minSec, maxSec);
                      syncStoresForVisibleWindow(minSec, maxSec, "zoom");
                      schedulePlotRedraw();
                    };
                    // Finer data for the brushed window loads first (the brush box stays up meanwhile),
                    // so the zoom lands on it instead of drawing the coarse trace and swapping.
                    const prep = props.onPrepareZoom?.(minSec * 1000, maxSec * 1000);
                    if (prep) {
                      void zoomWhenDetailReady(prep, commit);
                    } else {
                      commit();
                    }
                  } else {
                    clearAllBrushes();
                  }
                } else {
                  clearSelection();
                  // Click-to-scrub is skipped when the playhead is hidden: it would move a line the user
                  // cannot see, and setSelectedTime from a non-'playback' caller pauses the clock.
                  if (!props.hideSelectedTimeLine) {
                    const tSec = u.posToVal(u.bbox.left + left, "x", true);
                    if (Number.isFinite(tSec) && requestTimeControl("timeseries")) {
                      setIsManualTimeChange(true);
                      setSelectedTime(new Date(tSec * 1000), "timeseries");
                    }
                  }
                  schedulePlotRedraw(u);
                }
                dragMoved = false;
              });
              over.addEventListener("pointercancel", () => {
                activePointerId = null;
                interactionState.brushDragging = false;
                scrubbingPlayhead = false;
                dragStartClient = null;
                dragStartLeft = null;
                dragMoved = false;
                interactionState.hoverLeft = null;
                clearSelection();
                schedulePlotRedraw(u);
              });
              over.addEventListener("pointerleave", () => {
                if (activePointerId != null) {return;}
                interactionState.hoverLeft = null;
                clearSelection();
                schedulePlotRedraw(u);
              });
            },
          ],
        },
      };
  }

  function recomputeGlobalDomainFromPlots(): { min: number; max: number } | null {
    const plotSlots = chartSlots().filter((s): s is ChartSlotPlot => s.kind === "plot");
    if (plotSlots.length === 0) {return null;}
    const globalMin = Math.min(...plotSlots.map((s) => s.built.fullMinSec));
    const globalMax = Math.max(...plotSlots.map((s) => s.built.fullMaxSec));
    fullDomainRef.current = { min: globalMin, max: globalMax };
    return { min: globalMin, max: globalMax };
  }

  function finalizePlotMount(globalMin: number, globalMax: number, host: HTMLElement) {
    applyXAll(globalMin, globalMax);
    setVisibleDomainSec([globalMin, globalMax]);

    const combinedMs = combinedRangeMsFromStore();
    if (combinedMs) {
      const [startMs, endMs] = combinedMs;
      const s0 = startMs / 1000;
      const s1 = endMs / 1000;
      if (Number.isFinite(s0) && Number.isFinite(s1) && s1 > s0) {
        const lo = Math.max(s0, globalMin);
        const hi = Math.min(s1, globalMax);
        if (hi > lo) {
          applyXAll(lo, hi);
          setVisibleDomainSec([lo, hi]);
          props.onZoomedChange(true);
        } else {
          applyXAll(globalMin, globalMax);
          props.onZoomedChange(true);
        }
      }
    } else {
      props.onZoomedChange(false);
    }

    const interactHost = props.layout === "fleet" ? host : rootEl;
    if (!wheelHandler && interactHost) {
      wheelHandler = (event: WheelEvent) => {
        // Panning a window that is scrolling on its own just fights the animation.
        if (props.disableTimeInteractions) {return;}
        if (!interactHost.contains(event.target as Node)) {return;}
        const u0 = firstActiveInstance();
        if (!u0) {return;}
        const min = u0.scales.x.min;
        const max = u0.scales.x.max;
        if (min == null || max == null) {return;}
        const full = fullDomainRef.current;
        const span = max - min;
        const fullSpan = full.max - full.min;
        const isZoomed =
          fullSpan > 0 &&
          (span < fullSpan - 0.05 || Math.abs(min - full.min) > 0.05 || Math.abs(max - full.max) > 0.05);
        if (!isZoomed) {return;}
        event.preventDefault();
        event.stopPropagation();
        const pan = span * 0.1;
        const deltaX = typeof event.deltaX === "number" ? event.deltaX : 0;
        const deltaY = typeof event.deltaY === "number" ? event.deltaY : 0;
        const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
        const dir = Math.sign(delta);
        let n0 = min + (dir * pan) / 1;
        let n1 = max + (dir * pan) / 1;
        if (n0 < full.min) {
          const shift = full.min - n0;
          n0 += shift;
          n1 += shift;
        }
        if (n1 > full.max) {
          const shift = n1 - full.max;
          n0 -= shift;
          n1 -= shift;
        }
        if (n0 < full.min) {n0 = full.min;}
        if (n1 > full.max) {n1 = full.max;}
        applyXAll(n0, n1);
        syncStoresForVisibleWindow(n0, n1, "pan");
        for (const u of instancesRef.current) {u.redraw();}
      };
      interactHost.addEventListener("wheel", wheelHandler, { passive: false, capture: true });
    }

    if (!dblClickHandler && interactHost) {
      dblClickHandler = (ev: MouseEvent) => {
        // Nothing to reset to: the live window is authoritative, not a zoom over a full domain.
        if (props.disableTimeInteractions) {return;}
        if (!interactHost?.contains(ev.target as Node)) {return;}
        ev.preventDefault();
        ev.stopImmediatePropagation();
        setIsManualTimeChange(false);
        const cuts = cutEvents();
        const cutting = isCut() && Array.isArray(cuts) && cuts.length > 0;
        const hasActiveSelection = hasSelection() || selectedRange().length > 0;
        if (shouldClearActiveSelectionFirst({ hasActiveCuts: cutting, hasActiveSelection })) {clearActiveSelection();}
        else {clearSelection();}
        applyXAll(fullDomainRef.current.min, fullDomainRef.current.max);
        syncStoresForVisibleWindow(fullDomainRef.current.min, fullDomainRef.current.max, "reset");
      };
      interactHost.addEventListener("dblclick", dblClickHandler, { capture: true });
    }

    const bridge: ExploreUPlotBridgeRefs = {
      resetZoomToFull: (syncSelectionStore = true) => {
        const full = fullDomainRef.current;
        applyXAll(full.min, full.max);
        if (syncSelectionStore) {
          syncStoresForVisibleWindow(full.min, full.max, "reset");
        }
      },
      redraw: () => {
        instancesRef.current.forEach((u) => {
          try {
            u.redraw();
          } catch {
            /* noop */
          }
        });
      },
      setZoomDomainFromMs: (startMs, endMs, syncSelectionStore = true) => {
        const nextMin = startMs / 1000;
        const nextMax = endMs / 1000;
        const current = firstActiveInstance();
        const currMin = current?.scales.x.min;
        const currMax = current?.scales.x.max;
        const sameDomain =
          currMin != null &&
          currMax != null &&
          Math.abs(currMin - nextMin) <= 1e-6 &&
          Math.abs(currMax - nextMax) <= 1e-6;
        if (sameDomain) {
          if (!syncSelectionStore) {return;}
          syncStoresForVisibleWindow(nextMin, nextMax, "zoom");
          return;
        }
        applyXAll(nextMin, nextMax);
        if (syncSelectionStore) {
          syncStoresForVisibleWindow(nextMin, nextMax, "zoom");
        } else {
          setVisibleDomainSec([nextMin, nextMax]);
        }
        schedulePlotRedraw();
      },
      getFullDomainMs: () => {
        const f = fullDomainRef.current;
        return [f.min * 1000, f.max * 1000];
      },
      getZoomDomainMs: () => {
        const u0 = firstActiveInstance();
        if (!u0 || u0.scales.x.min == null || u0.scales.x.max == null) {return null;}
        return [u0.scales.x.min * 1000, u0.scales.x.max * 1000];
      },
    };
    bridgeRef = bridge;
    props.onBridgeReady(bridge);
    refreshPerChartLegends();

    if (instancesRef.current.length > 0) {
      savedClearTimeSeriesBrushSlot =
        typeof window.clearTimeSeriesBrush === "function"
          ? { hadPrevious: true, fn: window.clearTimeSeriesBrush }
          : { hadPrevious: false };
      window.clearTimeSeriesBrush = () => {
        clearAllBrushes();
      };
    }
  }

  /** Resolved after each data sync reaches the plots (see zoomWhenDetailReady). */
  let dataSyncWaiters: Array<() => void> = [];
  function notifyDataSynced() {
    const waiters = dataSyncWaiters;
    dataSyncWaiters = [];
    for (const w of waiters) {w();}
  }
  /** Resolves true on the next data sync, false if `timeoutMs` passes first. */
  function waitForDataSync(timeoutMs: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const t = setTimeout(() => resolve(false), timeoutMs);
      dataSyncWaiters.push(() => {
        clearTimeout(t);
        resolve(true);
      });
    });
  }

  /** Bumped by a new brush / reset so a superseded pending brush zoom never lands. */
  let pendingBrushZoomToken = 0;
  /** Releases this chart's hold on the page wait cursor (see acquireBrushBusyCursor). */
  let releaseBrushBusy: (() => void) | null = null;

  function releaseHeldDataSync() {
    brushZoomPending = false;
    if (deferredDataSync) {
      deferredDataSync = false;
      syncDataFromRevisionInner();
    }
  }

  async function zoomWhenDetailReady(prep: Promise<boolean>, commit: () => void) {
    const token = ++pendingBrushZoomToken;
    releaseBrushBusy?.();
    const release = acquireBrushBusyCursor();
    releaseBrushBusy = release;
    brushZoomPending = true;
    const startedMs = performance.now();
    let detailTimedOut = false;
    let syncTimedOut = false;
    try {
      const changed = await Promise.race([
        prep.catch(() => false),
        new Promise<boolean>((r) =>
          setTimeout(() => {
            detailTimedOut = true;
            r(false);
          }, BRUSH_DETAIL_WAIT_MS),
        ),
      ]);
      if (changed && token === pendingBrushZoomToken) {
        syncTimedOut = !(await waitForDataSync(BRUSH_DATA_SYNC_WAIT_MS));
      }
    } finally {
      debug("[ExploreTimeSeriesUPlot] brush zoom committed", {
        waitedMs: Math.round(performance.now() - startedMs),
        detailTimedOut,
        syncTimedOut,
        superseded: token !== pendingBrushZoomToken,
      });
      if (token === pendingBrushZoomToken) {
        // Zoom first, then the held data — same task, so the browser paints them as one frame.
        brushZoomPending = false;
        commit();
        releaseHeldDataSync();
        // Hold the wait cursor until the zoomed frame has painted (redraws are rAF-scheduled).
        requestAnimationFrame(() => requestAnimationFrame(() => {
          release();
          if (releaseBrushBusy === release) {releaseBrushBusy = null;}
        }));
      } else {
        release();
      }
    }
  }

  function syncDataFromRevision() {
    if (brushZoomPending) {
      deferredDataSync = true;
      notifyDataSynced();
      return;
    }
    try {
      syncDataFromRevisionInner();
    } finally {
      notifyDataSynced();
    }
  }

  function syncDataFromRevisionInner() {
    const host = props.getHostElement();
    if (!rootEl || !host) {return;}

    const slots = chartSlots();
    if (slots.length === 0) {return;}

    const slotCountChanged = slotWrapsRef.current.length !== slots.length;
    if (slotCountChanged || slotWrapsRef.current.some((w) => w == null)) {
      mountPlots();
      return;
    }

    let promoted = false;
    let domainChanged = false;

    for (const slot of slots) {
      const wrap = slotWrapsRef.current[slot.index];
      if (!wrap) {continue;}

      if (slot.kind === "placeholder") {
        if (!wrap.classList.contains("explore-uplot-chart-placeholder")) {
          mountPlots();
          return;
        }
        continue;
      }

      const existing = instancesByIndexRef.current[slot.index];
      if (existing) {
        try {
          existing.setData(slot.built.data, false);
          existing.redraw();
        } catch {
          mountPlots();
          return;
        }
      } else if (wrap.classList.contains("explore-uplot-chart-placeholder")) {
        mountPlotSlot(wrap, slot, host, props.timezone);
        promoted = true;
        domainChanged = true;
      } else {
        mountPlots();
        return;
      }
    }

    refreshInstancesFlatList();

    if (domainChanged || promoted) {
      const domain = recomputeGlobalDomainFromPlots();
      if (domain) {
        // `host` matters: finalizePlotMount derives `interactHost` from it in fleet layout, and that
        // is what the wheel-pan / dblclick-reset handlers attach to. Omitting it (as the upstream
        // copy did) left interactHost undefined after a placeholder -> plot promotion, silently
        // dropping both interactions for the rest of the session.
        finalizePlotMount(domain.min, domain.max, host);
        return;
      }
    }

    scheduleLegendRefresh();
    schedulePlotRedraw();
  }

  createEffect(
    on(
      () =>
        [
          props.visualEpoch,
          props.timezone,
          props.charts?.length ?? 0,
          (props.charts ?? [])
            .map(
              (c, i) =>
                `${i}:${normalizeChartHeight(c.chartHeight)}:${(c.series ?? []).length}`,
            )
            .join("|"),
        ] as const,
      () => {
        queueMicrotask(() => mountPlots());
      },
    ),
  );

  createEffect(
    on(
      () => props.dataRevision ?? 0,
      (rev, prev) => {
        if (rev === prev && rev !== 0) {return;}
        if (rev === 0) {return;}
        queueMicrotask(() => syncDataFromRevision());
      },
    ),
  );

  createEffect(() => {
    // A chart that does not publish a selection must not follow one either, or a brush drawn in another
    // window (the store is cross-window synced) would zoom this one from under the user.
    if (props.suppressSelectionStore) {return;}
    void selectedRange();
    void selectedRanges();
    void hasSelection();
    const combinedMs = combinedRangeMsFromStore();
    const bridge = bridgeRef;
    if (!bridge?.setZoomDomainFromMs || !combinedMs) {return;}
    const [startMs, endMs] = combinedMs;
    bridge.setZoomDomainFromMs(startMs, endMs, false);
    props.onZoomedChange(true);
  });

  // Resuming drops a zoom taken while paused: the window goes back to scrolling, and a held zoom would
  // otherwise outrank it again the moment the user paused.
  createEffect(() => {
    if (!props.disableTimeInteractions) {return;}
    if (!userZoomActive) {return;}
    userZoomActive = false;
    props.onZoomedChange(false);
  });

  createEffect(() => {
    visibleDomainSec();
    builtCharts();
    props.visualEpoch;
    const t = selectedTime();
    void t;
    scheduleLegendRefresh();
    if (storeRedrawRafId != null) {return;}
    storeRedrawRafId = requestAnimationFrame(() => {
      storeRedrawRafId = null;
      for (const inst of instancesRef.current) {
        try {
          inst.redraw();
        } catch {
          /* noop */
        }
      }
    });
  });

  onMount(() => {
    debug("ExploreTimeSeriesUPlot: mounted");
  });

  onCleanup(() => {
    pendingBrushZoomToken += 1;
    releaseBrushBusy?.();
    releaseBrushBusy = null;
    cancelScheduledRedraws();
    bridgeRef = null;
    destroyPlots();
    props.onBridgeReady(null);
  });

  return (
    <div class="explore-uplot-root" ref={(el) => (rootEl = el)}>
      <Show when={chartSlots().length === 0}>
        <Show
          when={props.awaitingData}
          fallback={
            <div class="explore-uplot-empty" role="status">
              No chart data
            </div>
          }
        >
          <div class="explore-uplot-loading" role="status" aria-live="polite">
            <div class="timeseries-chart-loading-row">
              <span class="timeseries-chart-loading-spinner" aria-hidden="true" />
              <span class="timeseries-chart-loading-label">Loading…</span>
            </div>
          </div>
        </Show>
      </Show>
    </div>
  );
}
